import { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Button, Alert, Chip, Divider, MenuItem, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, CircularProgress, Tooltip,
  IconButton, useMediaQuery, useTheme,
} from '@mui/material';
import { Add, Send, Cancel, BeachAccess } from '@mui/icons-material';
import axios from 'axios';
import { locale } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLOR = {
  'Open': 'default', 'Pending Approval': 'warning', 'Approved': 'success',
  'Rejected': 'error', 'Cancelled': 'default', 'Recalled': 'secondary',
};

// ── date helpers (mirror LeaveApplicationList) ──────────────────────────────
const workingDays = (start, end) => {
  if (!start || !end) return 0;
  let count = 0;
  const d = new Date(start); d.setHours(0, 0, 0, 0);
  const e = new Date(end);   e.setHours(0, 0, 0, 0);
  if (e < d) return 0;
  while (d <= e) { if (d.getDay() !== 0 && d.getDay() !== 6) count++; d.setDate(d.getDate() + 1); }
  return count;
};
const calDays = (a, b) => {
  if (!a || !b) return 0;
  const s = new Date(a); s.setHours(0, 0, 0, 0);
  const e = new Date(b); e.setHours(0, 0, 0, 0);
  if (e < s) return 0;
  return Math.round((e - s) / 86400000) + 1;
};
const addCalDays = (dateStr, days) => {
  if (!dateStr || !days) return '';
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days - 1);
  return d.toISOString().split('T')[0];
};
const fmt = (d) => d ? new Date(d).toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const balColor = (b) => b <= 0 ? 'error' : b <= 5 ? 'warning' : 'success';
const emptyForm = { leaveTypeCode: '', startDate: '', endDate: '', relieverNo: '', reason: '' };

export default function StaffLeave() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [types,     setTypes]     = useState([]);
  const [balances,  setBalances]  = useState([]);
  const [apps,      setApps]      = useState([]);
  const [employees, setEmployees] = useState([]);
  const [workflowOn, setWorkflowOn] = useState(false);
  const [notLinked, setNotLinked] = useState('');

  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  const [dlgOpen, setDlgOpen] = useState(false);
  const [form,    setForm]    = useState(emptyForm);
  const [saving,  setSaving]  = useState(false);
  const [formErr, setFormErr] = useState('');

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3500); };

  const loadApps = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/leave/my/applications`, { headers: hdrs() });
      setApps(data || []);
    } catch (err) {
      if (err.response?.status === 400) setNotLinked(err.response.data.error);
      else setError('Failed to load your applications');
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [t, b, e, wf] = await Promise.all([
          axios.get(`${API}/api/leave/my/leave-types`, { headers: hdrs() }),
          axios.get(`${API}/api/leave/my/balances`,    { headers: hdrs() }),
          axios.get(`${API}/api/employees`,            { headers: hdrs() }).catch(() => ({ data: [] })),
          axios.get(`${API}/api/approvals/enabled/LeaveApplication`, { headers: hdrs() }).catch(() => ({ data: {} })),
        ]);
        setTypes(t.data || []);
        setBalances(b.data || []);
        setEmployees(e.data || []);
        setWorkflowOn(!!wf.data?.enabled);
      } catch { /* handled per-call */ }
      await loadApps();
      setLoading(false);
    })();
  }, [loadApps]);

  const selectedType = types.find(t => t.code === form.leaveTypeCode) || null;
  const isFixed = !!(selectedType && selectedType.maxDaysPerApplication > 0);
  const reqDays = isFixed ? calDays(form.startDate, form.endDate) : workingDays(form.startDate, form.endDate);
  const myBalance = balances.find(b => b.leaveTypeCode === form.leaveTypeCode)?.balance;

  const handleField = (e) => {
    const { name, value } = e.target;
    setForm(f => {
      const next = { ...f, [name]: value };
      const lt = types.find(t => t.code === (name === 'leaveTypeCode' ? value : f.leaveTypeCode));
      const maxDays = lt?.maxDaysPerApplication || 0;
      if (name === 'startDate' && maxDays > 0 && value) next.endDate = addCalDays(value, maxDays);
      if (name === 'leaveTypeCode' && maxDays > 0 && f.startDate) next.endDate = addCalDays(f.startDate, maxDays);
      return next;
    });
    setFormErr('');
  };

  const openDlg = () => { setForm(emptyForm); setFormErr(''); setDlgOpen(true); };

  const submitApplication = async () => {
    if (!form.leaveTypeCode || !form.startDate || !form.endDate) {
      setFormErr('Leave type, start and end dates are required'); return;
    }
    if (selectedType?.requiresReliever && !form.relieverNo) {
      setFormErr(`${selectedType.name} requires a reliever — please assign one`); return;
    }
    setSaving(true); setFormErr('');
    try {
      await axios.post(`${API}/api/leave/my/applications`, form, { headers: hdrs() });
      setDlgOpen(false);
      flash('Leave application created (status: Open). Submit it for approval when ready.');
      await Promise.all([loadApps(), refreshBalances()]);
    } catch (err) {
      setFormErr(err.response?.data?.error || 'Could not create application');
    } finally { setSaving(false); }
  };

  const refreshBalances = async () => {
    try {
      const { data } = await axios.get(`${API}/api/leave/my/balances`, { headers: hdrs() });
      setBalances(data || []);
    } catch { /* ignore */ }
  };

  const submitForApproval = async (app) => {
    try {
      if (workflowOn) {
        await axios.post(`${API}/api/approvals/send`,
          { documentType: 'LeaveApplication', documentId: app._id }, { headers: hdrs() });
        flash('Sent for approval');
      } else {
        await axios.put(`${API}/api/leave/my/applications/${app._id}/submit`, {}, { headers: hdrs() });
        flash('Submitted for approval');
      }
      loadApps();
    } catch (err) { setError(err.response?.data?.error || 'Failed to submit'); }
  };

  const cancelApplication = async (app) => {
    try {
      await axios.put(`${API}/api/leave/my/applications/${app._id}/cancel`, {}, { headers: hdrs() });
      flash('Application cancelled');
      loadApps();
    } catch (err) { setError(err.response?.data?.error || 'Failed to cancel'); }
  };

  if (loading) return (
    <Box sx={{ textAlign: 'center', mt: 8 }}><CircularProgress /><Typography mt={2}>Loading…</Typography></Box>
  );

  if (notLinked) return (
    <Box sx={{ maxWidth: 700, mx: 'auto', mt: 6, px: 2 }}>
      <Alert severity="warning">{notLinked}</Alert>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: { xs: 1.5, sm: 4 }, mb: 4, px: { xs: 1.5, sm: 2 } }}>
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Balances */}
      <Card elevation={3} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <BeachAccess sx={{ color: '#1565c0' }} />
            <Typography variant="h6" fontWeight={700}>My Leave Balances</Typography>
          </Box>
          <Divider sx={{ mb: 1.5 }} />
          {balances.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No balances for the current year.</Typography>
          ) : (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {balances.map(b => (
                <Chip key={b.leaveTypeCode} label={`${b.leaveTypeName}: ${b.balance}d`}
                  color={balColor(b.balance)} variant="outlined" />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Applications */}
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="h6" fontWeight={700}>My Leave Applications</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={openDlg}>Apply for Leave</Button>
          </Box>
          {apps.length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={3} textAlign="center">No applications yet.</Typography>
          ) : isMobile ? (
            /* ── Phone: card list ── */
            <Box display="flex" flexDirection="column" gap={1.2}>
              {apps.map(a => (
                <Card key={a._id} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.6}>
                      <Typography variant="body2" fontWeight={700} fontFamily="monospace" noWrap>{a.applicationNo}</Typography>
                      <Chip label={a.status} size="small" color={STATUS_COLOR[a.status] || 'default'} sx={{ ml: 'auto' }} />
                    </Box>
                    <Typography variant="body2" fontWeight={600}>{a.leaveTypeName}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {fmt(a.startDate)} – {fmt(a.endDate)} · {a.leaveDays} day{a.leaveDays !== 1 ? 's' : ''}
                    </Typography>
                    {['Open', 'Pending Approval'].includes(a.status) && (
                      <Box display="flex" gap={1} mt={1}>
                        {a.status === 'Open' && (
                          <Button size="small" variant="outlined" color="info" startIcon={<Send />}
                            onClick={() => submitForApproval(a)} sx={{ flex: 1 }}>Submit</Button>
                        )}
                        <Button size="small" variant="outlined" color="inherit" startIcon={<Cancel />}
                          onClick={() => cancelApplication(a)} sx={{ flex: 1 }}>Cancel</Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 680 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Ref</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Period</strong></TableCell>
                    <TableCell align="center"><strong>Days</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apps.map(a => (
                    <TableRow key={a._id} hover>
                      <TableCell><Typography variant="caption" fontFamily="monospace" fontWeight={700}>{a.applicationNo}</Typography></TableCell>
                      <TableCell>{a.leaveTypeName}</TableCell>
                      <TableCell><Typography variant="caption">{fmt(a.startDate)} – {fmt(a.endDate)}</Typography></TableCell>
                      <TableCell align="center"><Chip label={a.leaveDays} size="small" color="primary" variant="outlined" /></TableCell>
                      <TableCell align="center"><Chip label={a.status} size="small" color={STATUS_COLOR[a.status] || 'default'} /></TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {a.status === 'Open' && (
                          <Tooltip title="Submit for Approval">
                            <IconButton size="small" color="info" onClick={() => submitForApproval(a)}><Send fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        {['Open', 'Pending Approval'].includes(a.status) && (
                          <Tooltip title="Cancel">
                            <IconButton size="small" color="default" onClick={() => cancelApplication(a)}><Cancel fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Apply dialog */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <BeachAccess fontSize="small" color="primary" />
            <Typography fontWeight={700}>Apply for Leave</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Leave Type *"
                name="leaveTypeCode" value={form.leaveTypeCode} onChange={handleField}
                SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}>
                {types.map(lt => (
                  <MenuItem key={lt.code} value={lt.code}>
                    {lt.name}
                    <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>
                      {lt.daysPerYear !== null ? `(${lt.daysPerYear}d/yr)` : '(unlimited)'}
                    </span>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {selectedType && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {selectedType.isPaid !== false ? '✓ Paid' : '✗ Unpaid'}
                    {selectedType.requiresReliever && <span style={{ color: '#e65100', fontWeight: 700 }}> · Reliever required</span>}
                    {selectedType.maxDaysPerApplication > 0 && ` · Max ${selectedType.maxDaysPerApplication} days`}
                  </Typography>
                  {selectedType.daysPerYear !== null && (
                    <Typography variant="body2" mt={0.3}>
                      Balance: <strong>{myBalance === undefined ? '0' : myBalance} days</strong>
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Start Date *" type="date"
                name="startDate" value={form.startDate} onChange={handleField} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="End Date *" type="date"
                name="endDate" value={form.endDate} onChange={handleField} InputLabelProps={{ shrink: true }}
                inputProps={{ min: form.startDate }}
                helperText={isFixed ? `Auto-filled · ${selectedType.maxDaysPerApplication} calendar days` : ''} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="caption" color="text.secondary">{isFixed ? 'Calendar days' : 'Working days'}</Typography>
                <Typography variant="h5" fontWeight={800} color={reqDays > 0 ? '#1565c0' : 'text.disabled'} lineHeight={1.2}>
                  {reqDays > 0 ? reqDays : '—'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small"
                label={selectedType?.requiresReliever ? 'Reliever *' : 'Reliever'}
                name="relieverNo" value={form.relieverNo} onChange={handleField}
                error={!!(selectedType?.requiresReliever && !form.relieverNo)}
                SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}>
                <MenuItem value=""><em>— None —</em></MenuItem>
                {employees
                  .filter(e => e.employeeNo && e.employeeNo !== (JSON.parse(localStorage.getItem('user') || '{}').employeeNo))
                  .map(e => (
                    <MenuItem key={e.employeeNo} value={e.employeeNo}>
                      {e.firstName} {e.lastName} ({e.employeeNo})
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Reason / Remarks" name="reason"
                value={form.reason} onChange={handleField} multiline rows={2} InputLabelProps={{ shrink: true }} />
            </Grid>
            {formErr && <Grid item xs={12}><Alert severity="error">{formErr}</Alert></Grid>}
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setDlgOpen(false)} size="small">Cancel</Button>
          <Button variant="contained" size="small" onClick={submitApplication} disabled={saving}
            startIcon={saving ? <CircularProgress size={14} /> : <Add fontSize="small" />}>
            {saving ? 'Saving…' : 'Create Application'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
